// app/components/thailand-premium-tapioca-starch-products/Hero.tsx
"use client";

import * as React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, Grid } from "@mui/material";
import { useTranslations, useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import Image from "next/image";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string;
};

const CMS_KEYS = [
  "ourproducts.hero.title",
  "ourproducts.hero.subtitle",
] as const;

type CmsKey = (typeof CMS_KEYS)[number];

export default function Hero() {
  const t = useTranslations("OurProducts");
  const locale = useLocale();
  const pathname = usePathname() || "/";

  // map pathname → CMS path (ตัด /en /zh ออกให้เหลือ path จริง)
  const pagePath = React.useMemo(() => {
    const parts = pathname.split("/");
    if (parts[1] === "en" || parts[1] === "zh") {
      const tail = parts.slice(2).join("/");
      return tail ? `/${tail}` : "/";
    }
    return pathname || "/";
  }, [pathname]);

  // ---------- default map (i18n) ----------
  const buildDefaultMap = React.useCallback((): Record<CmsKey, string> => {
    return {
      "ourproducts.hero.title": t("Hero.title"),
      "ourproducts.hero.subtitle": t("Hero.subtitle"),
    };
  }, [t]);

  const [inline, setInline] = React.useState<Record<string, string>>(
    () => buildDefaultMap()
  );

  // reset เมื่อ locale เปลี่ยน
  React.useEffect(() => {
    setInline(buildDefaultMap());
  }, [locale, buildDefaultMap]);

  React.useEffect(() => {
    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${
            process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID
          }&path=${encodeURIComponent(pagePath)}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          const key =
            e.cmsKey ||
            e.selector?.match(/data-cms-key="([^"]+)"/)?.[1] ||
            undefined;
          if (key) map[key] = e.value;
        });

        if (Object.keys(map).length > 0) {
          setInline((prev) => ({ ...prev, ...map }));
        }
      } catch (err) {
        console.warn("[OurProducts Hero] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  const txt = (key: CmsKey) => inline[key] ?? "";

  return (
    <Box id="tapioca-starch" className="our-products-hero">
      {/* ===== Desktop hero (ข้อความ + BG จาก CSS) ===== */}
      <Container
        className="container"
        sx={{ display: { xs: "none", lg: "block" } }}
      >
        <Grid container>
          <Grid size={12}>
            <Typography
              variant="h1"
              data-aos="zoom-in"
              data-cms-key="ourproducts.hero.title"
              sx={{
                textAlign: { xs: "center", md: "left" }
              }}
            >
              {txt("ourproducts.hero.title")}
            </Typography>
            <Typography
              variant="h2"
              data-aos="zoom-in"
              data-cms-key="ourproducts.hero.subtitle"
              sx={{
                textAlign: { xs: "center", md: "left" }
              }}
            >
              {txt("ourproducts.hero.subtitle")}
            </Typography>
          </Grid>
        </Grid>
      </Container>
      {/* ===== Tablet hero (ข้อความซ้าย 50% บนภาพ tablet) ===== */}
      <Container
        sx={{
          display: { xs: "none", sm: "block", lg: "none" },
          px: "0 !important",
          position: "relative",
        }}
      >
        <Box sx={{ position: "relative" }}>
          <Image
            src="/assets/thailand-premium-tapioca-starch-products/hero-tablet-bg.jpg"
            title="our-products-tapioca"
            alt="Premium Tapioca Starch — Bamboo Brand 50 kg bags by Poon Phol, Thailand"
            width={834}
            height={486}
            className="img-fluid"
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              px: { sm: 4, md: 6 },
            }}
          >
            <Grid container>

              <Grid size={{ xs: 12 }}>
                <Typography
                  variant="h1"
                  component="div"
                  data-aos="zoom-in"
                  data-cms-key="ourproducts.hero.title"
                  sx={{
                    textAlign: "left"
                  }}
                >
                  {txt("ourproducts.hero.title")}
                </Typography>
                <Typography
                  variant="h2"
                  data-aos="zoom-in"
                  data-cms-key="ourproducts.hero.subtitle"
                  sx={{
                    textAlign: "left"
                  }}
                >
                  {txt("ourproducts.hero.subtitle")}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Container>
      {/* ===== Mobile hero (ข้อความลอยบนภาพ mobile) ===== */}
      <Container
        sx={{
          display: { xs: "block", sm: "none" },
          px: "0 !important",
          position: "relative",
        }}
      >
        <Box sx={{ position: "relative" }}>
          <Image
            src="/assets/thailand-premium-tapioca-starch-products/hero-mobile-bg.jpg"
            title="our-products-tapioca-mobile"
            alt="Premium Tapioca Starch — Bamboo Brand bags from a Thailand tapioca starch exporter"
            width={618}
            height={572}
            className="img-fluid"
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
              pt: 2,
              px: 0,
            }}
          >
            <Typography
              variant="h1"
              component="div"
              data-aos="zoom-in"
              data-cms-key="ourproducts.hero.title"
              sx={{
                textAlign: "center"
              }}
            >
              {txt("ourproducts.hero.title")}
            </Typography>
            <Typography
              variant="h2"
              data-aos="zoom-in"
              data-cms-key="ourproducts.hero.subtitle"
              sx={{
                textAlign: "center"
              }}
            >
              {txt("ourproducts.hero.subtitle")}
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
